import {
  DefaultMetricOptions,
  Goal,
  ID,
  MetricEntry,
  MetricEntryMode,
  Task,
  Timestamp,
} from '@griply/types';
import { startOfDay } from 'date-fns';

import { calculateMetricEntryValue } from './calculate-metric-entry-value';
import { formatDateKey } from './format-date-key';
import { getCompletionPercentage } from './get-completion-percentage';

export type ComputeGoalSubgoalProgressOptions = {
  goal: Goal;
  subGoals: Goal[];
  tasks: Task[];
};

export type ComputeGoalSubgoalProgressResult = {
  entries: MetricEntry[];
  target: number;
};

enum EventTypes {
  Task = 'task',
  Goal = 'goal',
  MetricEntry = 'metricEntry',
}

type Event = {
  goalId: ID;
  date: Timestamp;
  value: number;
  type: EventTypes;
};

export const computeGoalSubgoalProgress = ({
  goal,
  subGoals,
  tasks,
}: ComputeGoalSubgoalProgressOptions): ComputeGoalSubgoalProgressResult => {
  const target = subGoals.length;
  const allGoals = [goal, ...subGoals];

  const goalIdGoalMap = allGoals.reduce<Record<ID, Goal>>((acc, g) => {
    acc[g.id] = g;
    return acc;
  }, {});

  const subGoalProgressMap = subGoals.reduce<Record<ID, number>>(
    (acc, subGoal) => {
      acc[subGoal.id] = 0;
      return acc;
    },
    {},
  );

  const goalIdTaskCountMap = allGoals.reduce<Record<ID, number>>(
    (acc, { id }) => {
      acc[id] = tasks.filter(({ goalId }) => goalId === id).length;
      return acc;
    },
    {},
  );

  const taskCompletedEvents = tasks
    .filter(({ completedAt, goalId }) => !!completedAt && goalId)
    .map<Event>(({ goalId, completedAt }) => ({
      goalId: goalId!,
      date: completedAt!,
      value: 1,
      type: EventTypes.Task,
    }));

  const subgoalCompletedEvents = subGoals
    .filter(({ completedAt }) => !!completedAt)
    .map<Event>(({ id, completedAt }) => ({
      goalId: id,
      date: completedAt!,
      value: 1,
      type: EventTypes.Goal,
    }));

  const metricEntryEvents = allGoals
    .filter(({ metric }) => !!metric?.entries)
    .map(({ id, metric }) =>
      metric?.entries?.map(({ id: entryId, date }) => ({
        goalId: id,
        value: calculateMetricEntryValue(
          entryId,
          metric?.entries ?? [],
          metric?.startValue ?? 0,
        ),
        date,
        type: EventTypes.MetricEntry,
      })),
    )
    .flat(1)
    .filter(Boolean) as unknown as Event[];

  const combinedEvents = [
    ...taskCompletedEvents,
    ...subgoalCompletedEvents,
    ...metricEntryEvents,
  ].sort((eventA, eventB) => eventA.date.getTime() - eventB.date.getTime());

  const dateEntryMap = combinedEvents.reduce<Record<string, MetricEntry>>(
    (acc, event) => {
      const dateKey = formatDateKey(event.date);
      const entry = (acc[dateKey] = acc[dateKey] ?? {
        id: dateKey,
        date: startOfDay(event.date),
        value: 0,
      });

      if (event.type === EventTypes.Goal) {
        subGoalProgressMap[event.goalId] = 100;

        const totalProgress = Object.values(subGoalProgressMap).reduce(
          (sum, subgoalProgress) => sum + subgoalProgress,
          0,
        );

        entry.value =
          (subGoals.length / 100) * (totalProgress / subGoals.length) || 0;

        return acc;
      }

      const eventGoal = goalIdGoalMap[event.goalId];
      const metricType =
        eventGoal.metric?.id ?? DefaultMetricOptions.CompletedTasks;

      if (
        event.type === EventTypes.Task &&
        metricType === DefaultMetricOptions.CompletedTasks
      ) {
        subGoalProgressMap[event.goalId] +=
          (event.value / goalIdTaskCountMap[event.goalId]) * 100;
      }

      if (event.type === EventTypes.MetricEntry) {
        subGoalProgressMap[event.goalId] = getCompletionPercentage({
          start: eventGoal.metric?.startValue ?? 0,
          current: event.value,
          end: eventGoal.metric?.targetValue ?? Infinity,
        });
      }

      subGoalProgressMap[event.goalId] = Math.min(
        subGoalProgressMap[event.goalId] ?? 0,
        100,
      );

      const totalProgress = Object.values(subGoalProgressMap).reduce(
        (sum, subgoalProgress) => sum + subgoalProgress,
        0,
      );
      entry.value =
        (subGoals.length / 100) * (totalProgress / subGoals.length) || 0;

      return acc;
    },
    {},
  );

  const goalStartDate = startOfDay(goal.startDate ?? goal.createdAt);

  const entries: MetricEntry[] = [
    {
      id: formatDateKey(goalStartDate),
      date: goalStartDate,
      value: 0,
      mode: MetricEntryMode.Absolute,
    },
    ...Object.values(dateEntryMap),
  ];

  return { entries, target };
};
